import { useState, useEffect } from "react";
import axios from "axios";
import { X, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

export default function PurchaseModal({ product, open, onClose }) {
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    talla: "",
    numero: "",
    nombre_camiseta: "",
    quantity: 1,
    notas: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset whenever the product changes / modal opens
  useEffect(() => {
    if (open) {
      setForm({
        nombre: "",
        email: "",
        telefono: "",
        talla: "",
        numero: "",
        nombre_camiseta: "",
        quantity: 1,
        notas: "",
      });
      setSuccess(false);
    }
  }, [open, product]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !product) return null;

  const setField = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email) {
      toast.error("Completá nombre y email para continuar.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/orders`, {
        product_slug: product.slug,
        product_name: product.name,
        price: product.price,
        quantity: Number(form.quantity) || 1,
        nombre: form.nombre,
        email: form.email,
        telefono: form.telefono || null,
        talla: form.talla || null,
        numero: form.numero || null,
        nombre_camiseta: form.nombre_camiseta || null,
        notas: form.notas || null,
      });
      setSuccess(true);
      toast.success("¡Solicitud enviada! Te contactamos para confirmar.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No se pudo registrar la solicitud. Probá nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
      data-testid="purchase-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        data-testid="purchase-modal-backdrop"
        className="absolute inset-0 bg-[#030712]/80 backdrop-blur-md"
      />

      {/* Panel */}
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-[2rem] bg-[#0A1121] border border-white/10 shadow-2xl">
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          data-testid="purchase-modal-close"
          className="absolute top-5 right-5 z-10 h-10 w-10 rounded-full grid place-items-center bg-[#030712]/60 backdrop-blur border border-white/10 text-white/70 hover:text-[#FFB800] hover:border-[#FFB800] transition-colors"
        >
          <X size={16} />
        </button>

        {/* Header with product preview */}
        <div className="relative grid grid-cols-1 sm:grid-cols-5 gap-4 p-4 sm:p-6 border-b border-white/5">
          <div className="sm:col-span-2 aspect-[4/3] sm:aspect-square rounded-[1.5rem] overflow-hidden bg-[#0B1D3A]">
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="sm:col-span-3 flex flex-col justify-center">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-2">
              {product.category} · Demo
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold uppercase tracking-tight leading-tight">
              {product.name}
            </h2>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-display text-2xl md:text-3xl font-extrabold text-[#FFB800]">
                {formatPrice(product.price)}
              </span>
              {product.oldPrice && (
                <span className="text-sm text-white/40 line-through">
                  {formatPrice(product.oldPrice)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        {success ? (
          <div className="p-8 sm:p-12 text-center" data-testid="purchase-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#FFB800] grid place-items-center text-[#030712] mb-6">
              <Check size={28} strokeWidth={3} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold uppercase tracking-tight mb-3">
              ¡Solicitud recibida!
            </h3>
            <p className="text-white/65 max-w-md mx-auto leading-relaxed">
              Recibimos tu pedido para <span className="text-white">{product.name}</span>.
              Te contactaremos por email para confirmar disponibilidad y enviarte
              el link de pago seguro.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="purchase-success-close"
              className="btn-primary mt-8 inline-flex"
            >
              Volver a la tienda
              <ArrowUpRight size={14} />
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-4 sm:p-8 space-y-1" data-testid="purchase-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <ModalField
                label="Nombre"
                idx="01"
                value={form.nombre}
                onChange={setField("nombre")}
                placeholder="Tu nombre"
                testId="modal-input-nombre"
                required
              />
              <ModalField
                label="Email"
                idx="02"
                type="email"
                value={form.email}
                onChange={setField("email")}
                placeholder="tu@email.com"
                testId="modal-input-email"
                required
              />
              <ModalField
                label="Teléfono"
                idx="03"
                value={form.telefono}
                onChange={setField("telefono")}
                placeholder="+34 600 000 000"
                testId="modal-input-telefono"
              />
              <ModalSelect
                label="Talla"
                idx="04"
                value={form.talla}
                onChange={setField("talla")}
                options={SIZES}
                testId="modal-input-talla"
              />
              <ModalField
                label="Número (1–99)"
                idx="05"
                value={form.numero}
                onChange={setField("numero")}
                placeholder="10"
                testId="modal-input-numero"
              />
              <ModalField
                label="Nombre en camiseta"
                idx="06"
                value={form.nombre_camiseta}
                onChange={setField("nombre_camiseta")}
                placeholder="Ej. CASTRO"
                testId="modal-input-nombre-camiseta"
              />
              <ModalField
                label="Cantidad"
                idx="07"
                type="number"
                value={form.quantity}
                onChange={setField("quantity")}
                placeholder="1"
                testId="modal-input-quantity"
                min={1}
                max={20}
              />
            </div>

            <div className="pt-4">
              <label className="flex justify-between items-baseline mb-2">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Notas
                </span>
                <span className="font-mono text-[10px] text-white/30">08</span>
              </label>
              <textarea
                rows={3}
                value={form.notas}
                onChange={setField("notas")}
                placeholder="Cualquier comentario adicional"
                data-testid="modal-input-notas"
                className="field-input resize-none"
              />
            </div>

            <div className="pt-6 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-[11px] text-white/45 max-w-sm leading-relaxed">
                Esto es un formulario demo: una vez confirmado, te enviaremos el
                link de pago seguro (Stripe) o coordinaremos por WhatsApp.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="purchase-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Enviando..." : "Confirmar solicitud"}
                <ArrowUpRight size={14} />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ModalField({
  label,
  idx,
  value,
  onChange,
  placeholder,
  type = "text",
  testId,
  required,
  min,
  max,
}) {
  return (
    <div className="pt-4">
      <label className="flex justify-between items-baseline mb-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
          {label}
          {required && <span className="text-[#FFB800]"> *</span>}
        </span>
        <span className="font-mono text-[10px] text-white/30">{idx}</span>
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="field-input"
        required={required}
        min={min}
        max={max}
      />
    </div>
  );
}

function ModalSelect({ label, idx, value, onChange, options, testId }) {
  return (
    <div className="pt-4">
      <label className="flex justify-between items-baseline mb-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
          {label}
        </span>
        <span className="font-mono text-[10px] text-white/30">{idx}</span>
      </label>
      <select
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="field-input bg-[#030712]"
      >
        <option value="">Elegir talla</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
